<template>
    <div class="d-flex flex-column h-100">
        <b-form-group :label="$t('objectives.objective')">
            <b-form-input
                v-model="$v.form.title.$model"
                :placeholder="$t('form.placeholder.writeTheTextHere')"
                :state="checkFieldState($v.form.title)"
            />
        </b-form-group>
        <Description
            :state="checkFieldState($v.form.description)"
            :value.sync="$v.form.description.$model"
            :label="$t('form.label.description')"
            :placeholder="$t('form.placeholder.description')"
            maxHeight
        />
        <Responsible
            :label="$t('form.label.owner')"
            :value.sync="$v.form.responsible.$model"
            :state="checkFieldState($v.form.responsible)"
            :placeholder="$t('form.placeholder.selectResponsible')"
        />
        <Levels
            :multiple="false"
            :value.sync="$v.form.level.$model"
            :state="checkFieldState($v.form.level)"
            :label="$t('form.label.objectiveLevel')"
            :placeholder="$t('form.placeholder.selectLevel')"
        />
        <Sections
            v-show="showSectionsSelect"
            :label="sectionsLabel"
            :multiple="false"
            :value.sync="$v.form.section.$model"
            :enabledSectionType="enabledSectionType"
            :state="checkFieldState($v.form.section)"
        />
        <Periods
            notGetActive
            lazyLoading
            applyScopeNotArchived
            :value.sync="$v.form.period.$model"
            :state="checkFieldState($v.form.period)"
            :placeholder="$t('form.placeholder.selectTheDesiredPeriod')"
            :label="$t('form.label.period')"
            :multiple="false"
            :hideSelectedOptions="false"
        />
        <ParentSelect
            :value.sync="$v.form.parent.$model"
            :state="checkFieldState($v.form.parent)"
            :label="$t('insights.linkToParent')"
            :objectiveId="objectiveId"
            size="sm"
            class="mt-2"
        />
        <div class="ml-auto mt-auto">
            <b-button variant="outline-primary" class="mr-2" :disabled="isSubmitting" @click="onClickCancel">{{
                $t('form.button.cancel')
            }}</b-button>
            <b-button variant="primary" :disabled="isSubmitting" @click="onClickSubmit">
                <span v-if="isSubmitting">
                    <i class="fa fa-spinner fa-spin" />
                </span>
                <span v-if="!isSubmitting"> {{ $t('form.button.save') }} </span>
            </b-button>
        </div>
    </div>
</template>
<script>
import { getObjective } from '../../api'
import { getParam } from '@/lib/url'
import { Levels, Periods, Sections, Users as Responsible, ParentSelect, Description } from '@/views/components/fields'
import { validationMixin } from 'vuelidate'
import { required, requiredIf } from 'vuelidate/lib/validators'

const levelIsNotIndividual = (vm) => {
    if (!vm.level) {
        return false
    }

    return ['unit', 'department'].includes(vm.level.value)
}

export default {
    mixins: [validationMixin],
    components: {
        Levels,
        Periods,
        Sections,
        Responsible,
        ParentSelect,
        Description,
    },
    data() {
        return {
            objectiveId: '',
            form: {
                title: null,
                level: null,
                period: null,
                responsible: null,
                section: null,
                parent: null,
                description: null,
            },
        }
    },
    validations: {
        form: {
            title: { required },
            level: { required },
            period: { required },
            responsible: { required },
            section: {
                required: requiredIf(levelIsNotIndividual),
            },
            parent: {},
            description: {},
        },
    },
    props: {
        isSubmitting: {
            type: Boolean,
            required: true,
        },
    },
    computed: {
        sectionsLabel() {
            const key = this.form.level && this.form.level.value == 'unit' ? 'organization' : 'team'

            return this.$t(`people.${key}`)
        },
        showSectionsSelect() {
            return levelIsNotIndividual(this.form)
        },
        enabledSectionType() {
            if (!this.form.level) {
                return 'department'
            }

            return this.form.level.value
        },
    },
    watch: {
        'form.level': function (level) {
            if (level.value == 'individual' || (this.form.section && level.value !== this.form.section['_type'])) {
                this.form.section = null
            }
        },
    },
    mounted() {
        this.objectiveId = getParam('objectiveId', this)
        this.setObjective()
    },
    methods: {
        onClickCancel() {
            this.$emit('cancel')
        },
        onClickSubmit() {
            if (!this.formIsValid()) {
                return
            }

            const payload = this.parseFormToApi()
            this.$emit('submit', this.objectiveId, payload)
        },
        formIsValid() {
            this.$v.$touch()
            return this.checkFieldState(this.$v.form)
        },
        checkFieldState(field) {
            return field.$dirty ? !field.$invalid : null
        },
        parseFormToApi() {
            return {
                id: this.objectiveId,
                title: this.form.title,
                level: this.form.level.value,
                period_id: this.form.period.value,
                user_id: this.form.responsible.value,
                section_id: this.form.section ? this.form.section.value : null,
                parent_id: this.form.parent ? this.form.parent.id : null,
                description: this.form.description,
            }
        },
        resetFormState() {
            this.$v.$reset()
        },
        updateLevel(level) {
            return {
                label: this.$t(`groupLevel.${level}`),
                value: level,
                isDefault: false,
            }
        },
        updatePeriod({ id, name }) {
            return {
                value: id,
                label: name,
            }
        },
        updateResponsible({ id, name, manager_id, occupation, picture }) {
            return {
                value: id,
                label: name,
                manager_id,
                occupation,
                picture,
            }
        },
        updateSection(section) {
            if (section != null) {
                return {
                    _type: section.type,
                    label: section.name,
                    value: section.id,
                }
            }
        },
        setObjective() {
            getObjective(this.objectiveId)
                .then(({ data }) => {
                    const { title, description, level, period, user, section, ancestors } = data
                    let parentObjective
                    if (ancestors.length) {
                        parentObjective = {
                            id: ancestors[ancestors.length - 1].id,
                            label: ancestors[ancestors.length - 1].title,
                        }
                    }
                    this.form = {
                        title,
                        description,
                        responsible: this.updateResponsible(user),
                        level: this.updateLevel(level),
                        period: this.updatePeriod(period),
                        section: this.updateSection(section),
                        parent: parentObjective,
                    }
                })
                .catch((error) => {
                    console.error(error)
                })
        },
    },
}
</script>
<style lang="scss" scoped>
.label-style {
    color: #3b3f4e;
    font-size: 16px;
    font-weight: 700;
}

::v-deep legend {
    @extend .label-style;
}

::v-deep label {
    @extend .label-style;
}

::v-deep .quill-editor.max-height {
    max-height: 130px !important;
}
</style>
